import React, { useState, useEffect, useRef } from 'react';
import { createChart, ColorType, LineStyle } from 'lightweight-charts';
import GexProfileOverlay from './GexProfileOverlay';

// --- LOGIC PORTED FROM TickerDetailView.jsx ---

const InteractiveStratChart = ({ ticker = "SPY", timeframe = "1D", initialData = null }) => {
    const chartContainerRef = useRef(null);
    const chartRef = useRef(null);
    const candleSeriesRef = useRef(null);
    const emaSeriesRef = useRef(null);
    const lineSeriesRefs = useRef([]);

    const [chartData, setChartData] = useState([]);
    const [emaData, setEmaData] = useState([]);
    const [broadeningLines, setBroadeningLines] = useState({ dtlLines: [], utlLines: [] });
    const [tickerDetails, setTickerDetails] = useState(null);
    const [gexLevels, setGexLevels] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedTimeframe, setSelectedTimeframe] = useState(timeframe);

    useEffect(() => {
        setSelectedTimeframe(timeframe);
    }, [timeframe]);

    // --- CLIENT-SIDE LOGIC HELPERS ---

    const calculateEMA = (data, period = 21) => {
        if (!data || data.length === 0) return [];
        const k = 2 / (period + 1);
        let emaArray = [];
        let currentEma = 0;

        // Calculate initial SMA for the first 'period' bars
        if (data.length >= period) {
            let sum = 0;
            for (let i = 0; i < period; i++) {
                sum += data[i].close;
            }
            currentEma = sum / period;
            emaArray.push({ time: data[period - 1].time, value: currentEma });
        } else {
            // Not enough data for the period, cannot calculate EMA
            return [];
        }

        // Calculate EMA for subsequent bars
        for (let i = period; i < data.length; i++) {
            const price = data[i].close;
            currentEma = price * k + currentEma * (1 - k);
            emaArray.push({ time: data[i].time, value: currentEma });
        }
        return emaArray;
    };

    // Ported from User's Pine Script: "NJ - Broadening v3"
    // Logic: 3-bar pivots. Connect Highs (DTL) and Lows (UTL).
    // Maintain max 2 lines per TF (user said "maxLinesPerTF = 2").
    const calculateBroadeningLines = (data) => {
        if (!data || data.length < 5) return { dtlLines: [], utlLines: [] };

        const pivLen = 3; // Corresponds to the '3' in ta.pivothigh(high, 3, 3)
        const maxLines = 2;
        let dtlLines = [];
        let utlLines = [];

        let pivotHighs = [];
        let pivotLows = [];

        // Identify pivots
        for (let i = pivLen; i < data.length - pivLen; i++) {
            const currentHigh = data[i].high;
            const currentLow = data[i].low;

            // Check High Pivot: currentHigh is the highest in the window [i-pivLen, i+pivLen]
            let isHighPivot = true;
            for (let j = 1; j <= pivLen; j++) {
                if (data[i - j].high > currentHigh || data[i + j].high > currentHigh) {
                    isHighPivot = false;
                    break;
                }
            }
            if (isHighPivot) {
                pivotHighs.push({ idx: i, price: currentHigh, time: data[i].time });
            }

            // Check Low Pivot: currentLow is the lowest in the window [i-pivLen, i+pivLen]
            let isLowPivot = true;
            for (let j = 1; j <= pivLen; j++) {
                if (data[i - j].low < currentLow || data[i + j].low < currentLow) {
                    isLowPivot = false;
                    break;
                }
            }
            if (isLowPivot) {
                pivotLows.push({ idx: i, price: currentLow, time: data[i].time });
            }
        }

        let lastPH1 = null; // Older pivot high
        let lastPH2 = null; // Newer pivot high

        // Process pivot highs to form DTLs (Descending Trend Lines or Broadening Highs)
        pivotHighs.forEach(ph => {
            if (lastPH2) {
                lastPH1 = lastPH2;
            }
            lastPH2 = ph;

            if (lastPH1 && lastPH2 && lastPH2.price > lastPH1.price) {
                // New Broadening High Line (DTL - broadening expansion)
                dtlLines.unshift({
                    p1: lastPH1,
                    p2: lastPH2,
                    color: '#3b82f6', // Blue
                    isLatest: true
                });
                if (dtlLines.length > maxLines) dtlLines.pop();
            }
        });

        let lastPL1 = null; // Older pivot low
        let lastPL2 = null; // Newer pivot low

        // Process pivot lows to form UTLs (Upward Trend Lines or Broadening Lows)
        pivotLows.forEach(pl => {
            if (lastPL2) {
                lastPL1 = lastPL2;
            }
            lastPL2 = pl;

            if (lastPL1 && lastPL2 && lastPL2.price < lastPL1.price) {
                // New Broadening Low Line (UTL - broadening expansion)
                utlLines.unshift({
                    p1: lastPL1,
                    p2: lastPL2,
                    color: '#3b82f6',
                    isLatest: true
                });
                if (utlLines.length > maxLines) utlLines.pop();
            }
        });

        // Format for Chart (project lines to the future/end of data)
        const formatLine = (p1, p2, data) => {
            // Calculate slope m based on bar index difference
            const m = (p2.price - p1.price) / (p2.idx - p1.idx);

            // Generate points from P1 to end of chart
            const lastIdx = data.length - 1;
            const finalPrice = p2.price + m * (lastIdx - p2.idx);
            const finalTime = data[lastIdx].time;

            // We only return start and end for the line series
            return [
                { time: p1.time, value: p1.price },
                { time: finalTime, value: finalPrice }
            ];
        };

        const processedDtl = dtlLines.map(l => ({
            data: formatLine(l.p1, l.p2, data),
            isLatest: true
        }));

        const processedUtl = utlLines.map(l => ({
            data: formatLine(l.p1, l.p2, data),
            isLatest: true
        }));

        return { dtlLines: processedDtl, utlLines: processedUtl };
    }

    // Fetch All Data or Use Initial Data
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);

            // OPTIMIZATION: Use pre-loaded data if available (e.g. from SwipeTicker)
            if (initialData && initialData.length > 0 && selectedTimeframe === '1D') {
                // Assuming initialData matches the structure of API response
                setChartData(initialData);
                setEmaData(calculateEMA(initialData, 21));
                setBroadeningLines(calculateBroadeningLines(initialData));
                setIsLoading(false);

                // Still try to fetch ticker details for BF flags if not provided?
                // For MVP, we skip details fetch if data is provided to be fast.
                return;
            }

            try {
                const API_BASE = import.meta.env.VITE_API_BASE_URL || '';
                const token = localStorage.getItem('stratiq_token');
                const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

                // Fetch details for BF flags (still from server)
                const detailsRes = await fetch(`${API_BASE}/api/ticker-details/${ticker}`, { headers });

                // Fetch GEX Levels
                const gexRes = await fetch(`${API_BASE}/api/gex-levels/${ticker}`, { headers });

                // Fetch candles
                const candlesRes = await fetch(`${API_BASE}/api/candles?ticker=${ticker}&timeframe=${selectedTimeframe}&limit=1400`, { headers });

                // Process Details
                if (detailsRes.ok) {
                    const details = await detailsRes.json();
                    setTickerDetails(details);
                }
                
                // Process GEX
                if (gexRes.ok) {
                    const gex = await gexRes.json();
                    if (gex && Object.keys(gex).length > 0) {
                        setGexLevels(gex);
                    } else {
                        setGexLevels(null);
                    }
                }

                // Process Candles
                if (candlesRes.ok) {
                    const rawData = await candlesRes.json();
                    if (Array.isArray(rawData) && rawData.length > 0) {
                        setChartData(rawData);

                        // 1. Calculate EMA Client-Side
                        const calculatedEMA = calculateEMA(rawData, 21);
                        setEmaData(calculatedEMA);

                        // 2. Calculate BF Lines Client-Side (Pine Logic)
                        const calculatedBF = calculateBroadeningLines(rawData);
                        setBroadeningLines(calculatedBF);

                    } else {
                        setChartData([]);
                        setEmaData([]);
                        setBroadeningLines({ dtlLines: [], utlLines: [] });
                    }
                }
            } catch (error) {
                console.error("Error fetching ticker data:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [ticker, selectedTimeframe, initialData]);

    // Initialize and Render Chart
    useEffect(() => {
        if (!chartContainerRef.current) return;
        if (chartData.length === 0) return;

        // Clean up previous chart
        if (chartRef.current) {
            try {
                chartRef.current.remove();
            } catch (e) { }
            chartRef.current = null;
        }

        const isDarkMode = !document.documentElement.classList.contains('light');
        const chart = createChart(chartContainerRef.current, {
            layout: {
                background: { type: ColorType.Solid, color: isDarkMode ? '#0b1121' : '#ffffff' },
                textColor: isDarkMode ? '#94a3b8' : '#64748b',
            },
            grid: {
                vertLines: { color: isDarkMode ? '#1e293b' : '#f1f5f9' },
                horzLines: { color: isDarkMode ? '#1e293b' : '#f1f5f9' },
            },
            width: chartContainerRef.current.clientWidth,
            height: chartContainerRef.current.clientHeight,
            timeScale: {
                borderColor: isDarkMode ? '#334155' : '#e2e8f0',
                visible: true,
                timeVisible: true,
                secondsVisible: false,
                rightOffset: 18,
                barSpacing: 8,
                minBarSpacing: 3,
                fixLeftEdge: false,
                fixRightEdge: false,
            },
            rightPriceScale: {
                borderColor: isDarkMode ? '#334155' : '#e2e8f0',
                autoScale: true,
                visible: true,
            },
        });
        chartRef.current = chart;

        // Add Candle Series
        const candleSeries = chart.addCandlestickSeries({
            upColor: '#10b981',
            downColor: '#ef4444',
            borderUpColor: '#10b981',
            borderDownColor: '#ef4444',
            wickUpColor: '#10b981',
            wickDownColor: '#ef4444',
        });
        candleSeriesRef.current = candleSeries;
        candleSeries.setData(chartData);

        // Force price scale
        chart.priceScale('right').applyOptions({
            autoScale: true,
            scaleMargins: {
                top: 0.1,
                bottom: 0.1,
            },
            alignLabels: true,
        });

        // Add EMA Series
        if (emaData.length > 0) {
            const emaSeries = chart.addLineSeries({
                color: '#f59e0b',
                lineWidth: 2,
                lastValueVisible: false,
                priceLineVisible: false,
            });
            emaSeriesRef.current = emaSeries;
            emaSeries.setData(emaData);
        }

        // Add GEX Levers
        if (gexLevels && candleSeriesRef.current) {
            if (gexLevels.call_wall) {
                candleSeriesRef.current.createPriceLine({
                    price: gexLevels.call_wall,
                    color: '#ef4444', // Red
                    lineWidth: 2,
                    lineStyle: LineStyle.Dashed,
                    axisLabelVisible: true,
                    title: 'Call Wall',
                });
            }
            if (gexLevels.put_wall) {
                candleSeriesRef.current.createPriceLine({
                    price: gexLevels.put_wall,
                    color: '#3b82f6', // Blue
                    lineWidth: 2,
                    lineStyle: LineStyle.Dashed,
                    axisLabelVisible: true,
                    title: 'Put Wall',
                });
            }
            if (gexLevels.zero_gamma) {
                candleSeriesRef.current.createPriceLine({
                    price: gexLevels.zero_gamma,
                    color: '#94a3b8', // Gray
                    lineWidth: 2,
                    lineStyle: LineStyle.Dotted,
                    axisLabelVisible: true,
                    title: 'Zero Gamma',
                });
            }
        }

        // Add Broadening Lines Logic
        lineSeriesRefs.current = [];

        // 1. Determine Highlighting Flags based on Timeframe
        let showUpper = false;
        let showLower = false;

        if (tickerDetails) {
            if (selectedTimeframe === '1D') {
                showUpper = tickerDetails.near_bf_d_upper;
                showLower = tickerDetails.near_bf_d_lower;
            } else if (selectedTimeframe === '1W') {
                showUpper = tickerDetails.near_bf_w_upper;
                showLower = tickerDetails.near_bf_w_lower;
            } else if (selectedTimeframe === '1M') {
                showUpper = tickerDetails.near_bf_m_upper;
                showLower = tickerDetails.near_bf_m_lower;
            }
        }

        // 2. Helper to find closest line using PROJECTION
        const currentBar = chartData[chartData.length - 1];
        const currentPrice = currentBar?.close || 0;
        const currentTime = currentBar?.time;

        const getProjectedValue = (line, targetTimeStr) => {
            if (!line || !line.data || line.data.length < 2) return null;
            const p2 = line.data[line.data.length - 1];
            const p1 = line.data[line.data.length - 2];
            const t2 = new Date(p2.time).getTime();
            const t1 = new Date(p1.time).getTime();
            const targetT = new Date(targetTimeStr).getTime();

            if (t2 === t1) return p2.value;
            const m = (p2.value - p1.value) / (t2 - t1);
            return m * (targetT - t2) + p2.value;
        };

        const showAnyBf = showUpper || showLower;
        let theClosestLine = null;
        let globalMinDiff = Infinity;

        if (showAnyBf) {
            const allLines = [
                ...(broadeningLines.dtlLines || []),
                ...(broadeningLines.utlLines || [])
            ];

            allLines.forEach(l => {
                if (!l.data || l.data.length === 0) return;
                let projVal = null;
                try {
                    projVal = getProjectedValue(l, currentTime);
                } catch (e) { }

                if (projVal === null || isNaN(projVal)) {
                    projVal = l.data[l.data.length - 1].value;
                }

                const diff = Math.abs(projVal - currentPrice);
                if (diff < globalMinDiff) {
                    globalMinDiff = diff;
                    theClosestLine = l;
                }
            });
        }

        // 3. Helper to Clamp Line Data
        let minPrice = Infinity;
        let maxPrice = -Infinity;
        chartData.forEach(d => {
            if (d.low < minPrice) minPrice = d.low;
            if (d.high > maxPrice) maxPrice = d.high;
        });
        if (minPrice === Infinity) { minPrice = 0; maxPrice = 100; }
        const clampMin = minPrice * 0.5;
        const clampMax = maxPrice * 2.0;

        const addLine = (lineData, color, isLatest, lineStyle, lineWidth) => {
            const clampedData = lineData.map(pt => ({
                time: pt.time,
                value: Math.max(clampMin, Math.min(clampMax, pt.value))
            }));
            const lineSeries = chart.addLineSeries({
                color: color,
                lineWidth: lineWidth,
                lineStyle: lineStyle,
                lastValueVisible: false,
                priceLineVisible: false,
                autoscaleInfoProvider: () => null,
            });
            lineSeries.setData(clampedData);
            lineSeriesRefs.current.push(lineSeries);
        };

        if (broadeningLines.dtlLines) {
            broadeningLines.dtlLines.forEach(l => {
                let color = '#3b82f6';
                let lineWidth = l.isLatest ? 2 : 1;
                // Highlight closest
                if (l === theClosestLine) {
                    color = '#f472b6'; // Pink-400
                    lineWidth = 3;
                }
                addLine(l.data, color, l.isLatest, l.isLatest ? LineStyle.Solid : LineStyle.Dotted, lineWidth);
            });
        }
        if (broadeningLines.utlLines) {
            broadeningLines.utlLines.forEach(l => {
                let color = '#3b82f6';
                let lineWidth = l.isLatest ? 2 : 1;
                // Highlight closest
                if (l === theClosestLine) {
                    color = '#f472b6'; // Pink-400
                    lineWidth = 3;
                }
                addLine(l.data, color, l.isLatest, l.isLatest ? LineStyle.Solid : LineStyle.Dotted, lineWidth);
            });
        }

        // Scaling
        const timeScale = chart.timeScale();
        const totalBars = chartData.length;
        const visibleBars = 85;

        if (totalBars > visibleBars) {
            timeScale.setVisibleLogicalRange({
                from: totalBars - visibleBars,
                to: totalBars + 2
            });
        } else {
            timeScale.fitContent();
        }

        // Observer
        const resizeObserver = new ResizeObserver(entries => {
            if (entries.length === 0 || !entries[0].contentRect) return;
            const { width, height } = entries[0].contentRect;
            chart.applyOptions({ width, height });
        });
        resizeObserver.observe(chartContainerRef.current);

        return () => {
            resizeObserver.disconnect();
            if (chartRef.current) {
                try {
                    chartRef.current.remove();
                } catch (e) { }
                chartRef.current = null;
            }
        };

    }, [chartData, emaData, broadeningLines, selectedTimeframe, tickerDetails, gexLevels]);

    return (
        <div className="flex flex-col h-full w-full relative">
            {/* Custom Toolbar */}
            <div className="absolute top-4 left-4 z-10 flex gap-2">
                <div className="flex bg-[var(--bg-surface)] backdrop-blur rounded-lg p-0.5 border border-[var(--border-primary)] shadow-lg">
                    {['1D', '1W', '1M'].map((tf) => (
                        <button
                            key={tf}
                            onClick={() => setSelectedTimeframe(tf)}
                            className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${selectedTimeframe === tf
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface)]/50'
                                }`}
                        >
                            {tf}
                        </button>
                    ))}
                </div>
            </div>

            {/* CHART BODY */}
            <div className="flex-1 relative min-h-[400px] z-10 bg-[var(--bg-primary)]">
                <div ref={chartContainerRef} className="absolute inset-0" />
                <GexProfileOverlay 
                    gexLevels={gexLevels} 
                    chartRef={chartRef} 
                    mainSeriesRef={candleSeriesRef}
                    chartContainerRef={chartContainerRef} 
                />
                
                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-[var(--bg-primary)] z-20">
                        <span className="text-[var(--text-muted)] animate-pulse">Loading {ticker}...</span>
                    </div>
                )}
                {!isLoading && chartData.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center bg-[var(--bg-primary)] z-20">
                        <span className="text-[var(--text-muted)]">No Data for {ticker}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InteractiveStratChart;
